import {
  CognitoIdentityClient,
  GetIdCommand,
  GetCredentialsForIdentityCommand,
} from "@aws-sdk/client-cognito-identity";
import { config } from "../config";
import type { AuthTokens, TemporaryCredentials, CreatedBy } from "./apiTypes";

/** Subset of Cognito/Entra ID-token claims we use to attribute changes. */
interface IdTokenClaims {
  email?: string;
  given_name?: string;
  family_name?: string;
  name?: string;
}

const COGNITO_PROVIDER = () => `cognito-idp.${config.region}.amazonaws.com/${config.cognitoUserPoolId}`;

/**
 * Cognito auth module matching the xlai pattern.
 *
 * Flow:
 * 1. Cognito hosted UI (Entra ID SAML) → ID token + access token
 * 2. Identity Pool → temporary AWS credentials (can only invoke Lambda)
 * 3. Client invokes Lambda directly with AWS SDK using those credentials
 */
export class AuthModule {
  private tokens: AuthTokens | null = null;
  private credentials: TemporaryCredentials | null = null;

  /** Restore tokens from sessionStorage (set by login page). */
  async restore(): Promise<boolean> {
    const stored = sessionStorage.getItem("mf-tokens");
    if (!stored) return false;
    try {
      const tokens = JSON.parse(stored);
      if (tokens.expiresAt <= Date.now()) return false;
      this.tokens = tokens;
      await this.getCognitoCredentials();
      return true;
    } catch {
      return false;
    }
  }

  /** Sign in via Cognito hosted UI. */
  async signIn(): Promise<AuthTokens> {
    const codeVerifier = this.generateCodeVerifier();
    const codeChallenge = await this.generateCodeChallenge(codeVerifier);
    const state = crypto.randomUUID();

    const authUrl =
      `https://${config.cognitoDomain}/oauth2/authorize?` +
      `response_type=code&client_id=${config.cognitoClientId}` +
      `&redirect_uri=${encodeURIComponent(config.appDomain + "/oauth2/idpresponse")}` +
      `&scope=openid+email&state=${state}` +
      `&code_challenge=${codeChallenge}&code_challenge_method=S256` +
      `&identity_provider=EntraID`;

    const code = await this.openDialogAndGetCode(authUrl, state);
    this.tokens = await this.exchangeCodeForTokens(code, codeVerifier);
    await this.getCognitoCredentials();
    return this.tokens;
  }

  signOut(): void {
    this.tokens = null;
    this.credentials = null;
    sessionStorage.removeItem("mf-tokens");
  }

  isAuthenticated(): boolean { return this.tokens !== null; }
  getTokens(): AuthTokens | null { return this.tokens; }

  /**
   * Identity of the signed-in user, derived from the Cognito ID token claims.
   * Used to attribute models / registers / runs to their real author instead
   * of a placeholder. Returns null when not signed in or the token can't be
   * decoded; callers should treat that as "unknown user".
   */
  getCurrentUser(): CreatedBy | null {
    if (!this.tokens) return null;
    const claims = this.decodeIdTokenClaims(this.tokens.idToken);
    if (!claims) return null;

    // Entra ID always issues given_name/family_name; fall back to splitting
    // `name` (or the email local-part) so we never store an empty author.
    let firstName = claims.given_name?.trim() ?? "";
    let lastName = claims.family_name?.trim() ?? "";
    if (!firstName && !lastName && claims.name) {
      const parts = claims.name.trim().split(/\s+/);
      firstName = parts[0] ?? "";
      lastName = parts.slice(1).join(" ");
    }
    if (!firstName && !lastName && claims.email) {
      firstName = claims.email.split("@")[0] ?? "";
    }
    if (!firstName && !lastName) return null;

    return {
      first_name: firstName,
      last_name: lastName,
      ...(claims.email ? { email: claims.email } : {}),
    };
  }

  /** Decode (without verifying — the IdP already signed it and CloudFront/the
   *  Identity Pool gate access) the JWT payload. Returns null on any error. */
  private decodeIdTokenClaims(idToken: string): IdTokenClaims | null {
    try {
      const payload = idToken.split(".")[1];
      if (!payload) return null;
      const base64 = payload.replace(/-/g, "+").replace(/_/g, "/");
      const json = decodeURIComponent(
        atob(base64)
          .split("")
          .map((c) => "%" + c.charCodeAt(0).toString(16).padStart(2, "0"))
          .join("")
      );
      return JSON.parse(json) as IdTokenClaims;
    } catch {
      return null;
    }
  }

  /** Get Cognito Identity Pool credentials for Lambda invocation. */
  async getCognitoCredentials(): Promise<TemporaryCredentials> {
    if (this.credentials && this.credentials.expiration > new Date()) {
      return this.credentials;
    }
    if (!this.tokens) throw new Error("Not authenticated.");

    const client = new CognitoIdentityClient({ region: config.region });
    const idResp = await client.send(
      new GetIdCommand({
        IdentityPoolId: config.cognitoIdentityPoolId,
        Logins: { [COGNITO_PROVIDER()]: this.tokens.idToken },
      })
    );
    const credsResp = await client.send(
      new GetCredentialsForIdentityCommand({
        IdentityId: idResp.IdentityId!,
        Logins: { [COGNITO_PROVIDER()]: this.tokens.idToken },
      })
    );
    const c = credsResp.Credentials!;
    this.credentials = {
      accessKeyId: c.AccessKeyId!,
      secretAccessKey: c.SecretKey!,
      sessionToken: c.SessionToken!,
      expiration: c.Expiration!,
    };
    return this.credentials;
  }

  /** Refresh tokens if within 5 min of expiry. */
  async ensureTokenValid(): Promise<void> {
    if (!this.tokens) throw new Error("Not authenticated.");
    if (Date.now() < this.tokens.expiresAt - 300_000) return;
    const resp = await fetch(`https://${config.cognitoDomain}/oauth2/token`, {
      method: "POST",
      headers: { "Content-Type": "application/x-www-form-urlencoded" },
      body: new URLSearchParams({
        grant_type: "refresh_token",
        client_id: config.cognitoClientId,
        refresh_token: this.tokens.refreshToken,
      }),
    });
    if (!resp.ok) throw new Error("Session expired. Please sign in again.");
    const d = await resp.json();
    this.tokens = {
      ...this.tokens,
      accessToken: d.access_token,
      idToken: d.id_token,
      expiresAt: Date.now() + d.expires_in * 1000,
    };
    this.credentials = null;
  }

  // ─── Token exchange ───────────────────────────────────────────────────────

  private async exchangeCodeForTokens(code: string, codeVerifier: string): Promise<AuthTokens> {
    const resp = await fetch(`https://${config.cognitoDomain}/oauth2/token`, {
      method: "POST",
      headers: { "Content-Type": "application/x-www-form-urlencoded" },
      body: new URLSearchParams({
        grant_type: "authorization_code",
        client_id: config.cognitoClientId,
        code,
        redirect_uri: `${config.appDomain}/oauth2/idpresponse`,
        code_verifier: codeVerifier,
      }),
    });
    if (!resp.ok) throw new Error("Failed to exchange auth code for tokens.");
    const d = await resp.json();
    return {
      accessToken: d.access_token,
      idToken: d.id_token,
      refreshToken: d.refresh_token,
      expiresAt: Date.now() + d.expires_in * 1000,
    };
  }

  // ─── Dialog / popup ───────────────────────────────────────────────────────

  private openDialogAndGetCode(url: string, expectedState: string): Promise<string> {
    if (typeof Office === "undefined" || !Office?.context?.host) return this.openPopupAndGetCode(url, expectedState);

    const dialogUrl = `${config.appDomain}/oauth2/redirect.html#${encodeURIComponent(url)}`;
    return new Promise((resolve, reject) => {
      Office.context.ui.displayDialogAsync(dialogUrl, { height: 60, width: 40 }, (result) => {
        if (result.status !== Office.AsyncResultStatus.Succeeded) {
          reject(new Error("Failed to open sign-in dialog."));
          return;
        }
        const dialog = result.value;
        dialog.addEventHandler(Office.EventType.DialogMessageReceived, (arg: unknown) => {
          dialog.close();
          const msgArg = arg as { message?: string };
          if (!msgArg.message) { reject(new Error("Sign-in failed.")); return; }
          const params = new URLSearchParams(msgArg.message);
          const code = params.get("code");
          const state = params.get("state");
          if (state !== expectedState) { reject(new Error("Invalid auth state.")); return; }
          if (!code) { reject(new Error("No auth code received.")); return; }
          resolve(code);
        });
        dialog.addEventHandler(Office.EventType.DialogEventReceived, () => reject(new Error("Sign-in dialog was closed.")));
      });
    });
  }

  private openPopupAndGetCode(url: string, expectedState: string): Promise<string> {
    return new Promise((resolve, reject) => {
      const popup = window.open(url, "auth", "width=500,height=600");
      if (!popup) { reject(new Error("Failed to open sign-in popup.")); return; }
      const cleanup = () => { window.removeEventListener("message", onMessage); clearInterval(closedCheck); };
      const onMessage = (event: MessageEvent) => {
        if (event.origin !== window.location.origin || event.data?.type !== "idpResponse") return;
        cleanup();
        popup.close();
        const params = new URLSearchParams(event.data.query);
        const state = params.get("state");
        const code = params.get("code");
        if (state !== expectedState) { reject(new Error("Invalid auth state.")); return; }
        if (!code) { reject(new Error("No auth code received.")); return; }
        resolve(code);
      };
      window.addEventListener("message", onMessage);
      const closedCheck = setInterval(() => { if (popup.closed) { cleanup(); reject(new Error("Sign-in popup was closed.")); } }, 500);
    });
  }

  // ─── PKCE ─────────────────────────────────────────────────────────────────

  private generateCodeVerifier(): string {
    const a = new Uint8Array(32);
    crypto.getRandomValues(a);
    return btoa(String.fromCharCode(...a)).replace(/\+/g, "-").replace(/\//g, "_").replace(/=+$/, "");
  }

  private async generateCodeChallenge(verifier: string): Promise<string> {
    const digest = await crypto.subtle.digest("SHA-256", new TextEncoder().encode(verifier));
    return btoa(String.fromCharCode(...new Uint8Array(digest))).replace(/\+/g, "-").replace(/\//g, "_").replace(/=+$/, "");
  }
}
